import React from 'react';

interface MetaTableProps {
  meta: Record<string, any>;
  title?: string;
  className?: string;
}

export default function MetaTable({ meta, title, className = '' }: MetaTableProps) {
  if (!meta || Object.keys(meta).length === 0) {
    return (
      <div className={`text-center py-4 text-gray-500 ${className}`}>
        <p>No metadata available</p>
      </div>
    );
  }

  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }
    
    if (typeof value === 'boolean') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value.toString()}
        </span>
      );
    }
    
    if (typeof value === 'number') {
      return <span className="font-mono">{value}</span>;
    }
    
    if (typeof value === 'string') {
      // Check if it's a JSON string
      try {
        const parsed = JSON.parse(value);
        return (
          <pre className="text-xs bg-gray-100 p-2 rounded whitespace-pre-wrap break-words">
            {JSON.stringify(parsed, null, 2)}
          </pre>
        );
      } catch {
        return <span className="break-words">{value}</span>;
      }
    }
    
    if (Array.isArray(value)) {
      return (
        <div className="space-y-1">
          {value.map((item, index) => (
            <div key={index} className="text-xs bg-gray-100 p-2 rounded">
              {renderValue(item)}
            </div>
          ))}
        </div>
      );
    }
    
    if (typeof value === 'object') {
      return (
        <pre className="text-xs bg-gray-100 p-2 rounded whitespace-pre-wrap break-words">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    
    return <span>{String(value)}</span>;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {title && (
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      )}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '250px' }}>
                Key
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Value
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.entries(meta).map(([key, value]) => (
              <tr key={key} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                  <code className="bg-gray-100 px-2 py-1 rounded text-md">
                    {key}
                  </code>
                </td>
                <td className="px-4 py-3 text-md text-gray-900 max-w-md">
                  {renderValue(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
